import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import type { Item, StockMovement, Warehouse } from '../../../types/bookkeeping.js';

export function KardexPage() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();

  const [itemId, setItemId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const { data: itemsData } = useQuery({
    queryKey: ['bookkeeping-items', activeOrgId],
    queryFn: () => api<{ items: Item[] }>('/bookkeeping/items'),
    enabled: !!activeOrgId,
  });
  const inventoryItems = (itemsData?.items ?? []).filter(
    (i) => i.type === 'inventory' && i.is_active,
  );

  const { data: warehousesData } = useQuery({
    queryKey: ['bk-warehouses', activeOrgId],
    queryFn: () => api<{ warehouses: Warehouse[] }>('/bookkeeping/inventory/warehouses'),
    enabled: !!activeOrgId,
  });
  const warehouses = warehousesData?.warehouses ?? [];

  const movementsParams = new URLSearchParams();
  if (itemId) movementsParams.set('item_id', itemId);
  if (warehouseId) movementsParams.set('warehouse_id', warehouseId);
  if (fromDate) movementsParams.set('from', fromDate);
  if (toDate) movementsParams.set('to', toDate);

  const { data: movementsData, isLoading } = useQuery({
    queryKey: ['bk-movements', activeOrgId, itemId, warehouseId, fromDate, toDate],
    queryFn: () =>
      api<{ movements: StockMovement[]; base_currency: string }>(
        `/bookkeeping/inventory/movements?${movementsParams.toString()}`,
      ),
    enabled: !!activeOrgId && !!itemId,
  });
  const movements = movementsData?.movements ?? [];
  const baseCurrency = movementsData?.base_currency ?? 'USD';

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('inventory.kardex.title')}</h1>

      {/* Filters */}
      <div className="grid grid-cols-4 gap-3">
        <select
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        >
          <option value="">— {t('inventory.kardex.item')}</option>
          {inventoryItems.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name}
            </option>
          ))}
        </select>
        <select
          value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        >
          <option value="">{t('inventory.kardex.all_warehouses')}</option>
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.code} — {w.name}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        />
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        />
      </div>

      {!itemId && (
        <p className="text-sm text-slate-400">{t('inventory.kardex.select_item_prompt')}</p>
      )}

      {itemId &&
        (isLoading ? (
          <p className="text-sm text-slate-500">{t('common:loading')}</p>
        ) : (
          <div className="overflow-x-auto rounded border bg-white">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t('inventory.kardex.occurred_at')}</th>
                  <th className="px-3 py-2">{t('inventory.kardex.movement_type')}</th>
                  <th className="px-3 py-2 text-right">{t('inventory.kardex.qty_delta')}</th>
                  <th className="px-3 py-2 text-right">{t('inventory.kardex.unit_cost')}</th>
                  <th className="px-3 py-2 text-right">{t('inventory.kardex.total_cost')}</th>
                  <th className="px-3 py-2">{t('inventory.kardex.currency')}</th>
                  <th className="px-3 py-2 text-right">{t('inventory.kardex.running_qty')}</th>
                  <th className="px-3 py-2 text-right">{t('inventory.kardex.running_avg')}</th>
                  <th className="px-3 py-2">{t('inventory.kardex.source')}</th>
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-4 text-center text-slate-400">
                      {t('inventory.kardex.empty')}
                    </td>
                  </tr>
                )}
                {movements.map((m) => {
                  const isForeign = m.currency !== baseCurrency;
                  // Display the base-currency cost as the primary number — that's
                  // what drives WAC/FIFO. Show the original-currency value as a
                  // secondary line only when the currencies differ.
                  return (
                    <tr key={m.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs">{m.occurred_at.slice(0, 10)}</td>
                      <td className="px-3 py-2">
                        {t(`inventory.kardex.types.${m.movement_type}`, {
                          defaultValue: m.movement_type,
                        })}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{m.qty_delta}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {m.unit_cost_base}
                        {isForeign && (
                          <div className="text-xs text-slate-400">
                            {m.unit_cost} {m.currency}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {m.total_cost_base}
                        {isForeign && (
                          <div className="text-xs text-slate-400">
                            {m.total_cost} {m.currency}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {isForeign ? m.currency : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{m.running_qty}</td>
                      <td className="px-3 py-2 text-right font-mono">{m.running_avg}</td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {t(`inventory.kardex.sources.${m.source_doc_type}`, {
                          defaultValue: m.source_doc_type,
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  );
}
